/**
 * Portail public de vérification du registre ESS
 * Accessible sans authentification via /verify/registry/:registryId
 */
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, AlertTriangle, Clock, FileText, Hash, User, Calendar, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  valid: { icon: CheckCircle, label: 'VALIDE', color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', ring: 'ring-emerald-100' },
  revoked: { icon: XCircle, label: 'REVOQUE', color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-200', ring: 'ring-red-100' },
  superseded: { icon: AlertTriangle, label: 'REMPLACE', color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-200', ring: 'ring-amber-100' },
  expired: { icon: Clock, label: 'EXPIRE', color: 'text-gray-600', bg: 'bg-gray-50', border: 'border-gray-200', ring: 'ring-gray-100' },
};

export default function RegistryVerificationPage() {
  const { registryId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (registryId) {
      fetch(`${API_URL}/api/registry/verify/${registryId}`)
        .then(r => r.json())
        .then(d => {
          if (d.success) setData(d);
          else setError(d.detail || 'Document non trouvé');
        })
        .catch(() => setError('Erreur de connexion au registre'))
        .finally(() => setLoading(false));
    }
  }, [registryId]);

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-center">
          <Shield className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">Verification en cours...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-red-100 p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Verification echouee</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <p className="text-sm text-gray-400">ID: {registryId}</p>
          <a href="/" className="inline-flex items-center gap-1.5 mt-6 text-sm text-blue-600 hover:text-blue-800">
            <ArrowLeft className="w-4 h-4" /> Retour
          </a>
        </div>
      </div>
    );
  }

  const statusConf = STATUS_CONFIG[data.status] || STATUS_CONFIG.expired;
  const StatusIcon = statusConf.icon;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="registry-verify-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white py-6 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-6 h-6 text-amber-400" />
            <h1 className="text-lg font-bold tracking-wide">REGISTRE DE VERIFICATION ESS</h1>
          </div>
          <p className="text-gray-400 text-sm">Portail de verification d'authenticite des documents</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-6">
        {/* Status Banner */}
        <div className={`rounded-xl border-2 ${statusConf.border} ${statusConf.bg} p-6 mb-6 text-center ring-4 ${statusConf.ring}`} data-testid="verify-status-banner">
          <StatusIcon className={`w-16 h-16 ${statusConf.color} mx-auto mb-3`} />
          <h2 className={`text-2xl font-bold ${statusConf.color}`} data-testid="verify-status-label">{statusConf.label}</h2>
          <p className="text-gray-600 mt-1 text-sm">
            {data.status === 'valid' && 'Ce document est authentique et valide dans le registre ESS.'}
            {data.status === 'revoked' && 'Ce document a ete revoque et n\'est plus valide.'}
            {data.status === 'superseded' && 'Ce document a ete remplace par une version plus recente.'}
            {data.status === 'expired' && 'Ce document a expire.'}
          </p>
        </div>

        {/* Document Details */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="verify-details">
          <div className="px-5 py-3 bg-gray-50 border-b">
            <h3 className="font-semibold text-sm text-gray-900 uppercase tracking-wide">Details du document</h3>
          </div>
          <div className="divide-y">
            <DetailRow icon={Shield} label="N° Registre" value={data.registry_id} mono />
            <DetailRow icon={FileText} label="Type document" value={data.doc_type} />
            <DetailRow icon={User} label="Emetteur" value={data.issuer} />
            <DetailRow icon={Hash} label="Empreinte SHA-256" value={data.hash_sha256} mono truncate />
            <DetailRow icon={Calendar} label="Scelle le" value={formatDate(data.sealed_at)} />
            <DetailRow icon={FileText} label="Version" value={data.version} />
            {data.revoked_at && (
              <DetailRow icon={XCircle} label="Revoque le" value={formatDate(data.revoked_at)} danger />
            )}
            {data.revoke_reason && (
              <DetailRow icon={AlertTriangle} label="Motif" value={data.revoke_reason} danger />
            )}
          </div>
        </div>

        {/* QR Code */}
        <div className="mt-6 bg-white rounded-xl border shadow-sm p-6 text-center">
          <p className="text-sm text-gray-500 mb-3">QR Code de verification</p>
          <img 
            src={`${API_URL}/api/registry/qr/${data.registry_id}`} 
            alt="QR Code" 
            className="w-32 h-32 mx-auto border rounded-lg"
            data-testid="verify-qr-code"
          />
          <p className="text-xs text-gray-400 mt-2 break-all">{data.public_url}</p>
        </div>

        {/* Footer */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">
            Registre de Verification ESS — O'SCOP Outremer / COPPAM
          </p>
          <a href="/" className="inline-flex items-center gap-1.5 mt-3 text-sm text-blue-600 hover:text-blue-800">
            <ArrowLeft className="w-4 h-4" /> Retour a l'accueil
          </a>
        </div>
      </div>
    </div>
  );
}

function DetailRow({ icon: Icon, label, value, mono, truncate: trunc, danger }) {
  return (
    <div className="flex items-start gap-3 px-5 py-3">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${danger ? 'text-red-500' : 'text-gray-400'}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-gray-500">{label}</p>
        <p className={`text-sm ${mono ? 'font-mono' : 'font-medium'} ${danger ? 'text-red-600' : 'text-gray-900'} ${trunc ? 'truncate' : ''}`} title={value}>
          {value || '-'}
        </p>
      </div>
    </div>
  );
}
